// features/employee/fund/components/BeneficiaryTable.jsx
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Flex, Form, Input, InputNumber } from "antd";
import { useTranslation } from "react-i18next";
import styles from "../styles/EmployeeFund.module.css";
import { MAX_BENEFICIARIES, emptyBeneficiary } from "../schemas/EmployeeFundSchemas";
import { FormSelect } from "components/form/FormProvide";
import { useSelector } from "react-redux";
import { useMemo, useState } from "react";

const BeneficiaryTable = ({ disabled = false }) => {
    const { t, i18n } = useTranslation();
    const form = Form.useFormInstance();
    const beneficiaries = Form.useWatch("beneficiaries", form) || [];

    const totalShare = beneficiaries.reduce((sum, b) => sum + (Number(b?.sharePercent) || 0), 0);
    const isComplete = beneficiaries.length > 0 && totalShare === 100;
    const relationshipOptions = useSelector(
        (state) => state.options.relationshipOptions);

    function modifyOptions(options = [], lang = "en") {
        return options.map((opt) => ({
            ...opt,
            label: lang === "en" ? opt.nameEn : opt.name,
            value: opt.id,
        }));
    }
    const modifiedRelationshipOptions = useMemo(() => {
        return modifyOptions(relationshipOptions, i18n?.language);
    }, [i18n, relationshipOptions]);

    return (
        <Form.List name="beneficiaries">
            {(fields, { add, remove }) => (
                <>
                    <Flex justify="space-between" align="center" style={{ marginBottom: 12 }}>
                        <div className={styles.sectionTitle} style={{ marginBottom: 0 }}>
                            {t("employeeFund.label.beneficiaries") || "ผู้รับผลประโยชน์"}{" "}
                            <span style={{ fontWeight: 400, color: "#667085", fontSize: 13 }}>
                                ({t("employeeFund.label.beneficiariesMax") || `สูงสุด ${MAX_BENEFICIARIES} คน`})
                            </span>
                        </div>
                        <Button
                            type="primary"
                            ghost
                            icon={<PlusOutlined />}
                            disabled={disabled || fields.length >= MAX_BENEFICIARIES}
                            onClick={() => add(emptyBeneficiary())}
                        >
                            {t("employeeFund.label.addBeneficiary") || "เพิ่มผู้รับผลประโยชน์"}
                        </Button>
                    </Flex>

                    {fields.length > 0 && (
                        <table className={styles.beneficiaryTable}>
                            <thead>
                                <tr>
                                    <th style={{ width: 40 }}>{t("employeeFund.label.order") || "ลำดับ"}</th>
                                    <th>{t("employeeFund.label.fullName") || "ชื่อ-นามสกุล"}</th>
                                    <th>{t("employeeFund.label.nationalId") || "เลขบัตรประชาชน"}</th>
                                    <th>{t("employeeFund.label.relationship") || "ความสัมพันธ์"}</th>
                                    <th style={{ width: 100 }}>{t("employeeFund.label.sharePercent") || "สัดส่วน (%)"}</th>
                                    <th>{t("employeeFund.label.address") || "ที่อยู่ปัจจุบัน / ติดต่อได้"}</th>
                                    <th style={{ width: 40 }} />
                                </tr>
                            </thead>
                            <tbody>
                                {fields.map(({ key, name, ...restField }, index) => (
                                    <tr key={key}>
                                        <td>{index + 1}</td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "fullName"]}
                                                rules={[{ required: true, message: "" }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input disabled={disabled} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} />
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "nationalId"]}
                                                rules={[{ required: true, message: "" }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <Input disabled={disabled} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} />
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <FormSelect
                                                required={true}
                                                name={[name, "relationship"]}
                                                disabled={disabled}
                                                // label={t('relationship')}
                                                placeholder={t('please-select-relationship')}
                                                // size="large"
                                                // sortBy="value"
                                                options={modifiedRelationshipOptions}
                                            />
                                        </td>
                                        <td>
                                            <Form.Item
                                                {...restField}
                                                name={[name, "sharePercent"]}
                                                rules={[{ required: true, message: "" }]}
                                                style={{ marginBottom: 0 }}
                                            >
                                                <InputNumber disabled={disabled} min={0} max={100} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </td>
                                        <td>
                                            <Form.Item {...restField} name={[name, "address"]} style={{ marginBottom: 0 }}>
                                                <Input.TextArea autoSize={1} disabled={disabled} placeholder={t("fund.placeholder.please_input") || "กรุณากรอก"} />
                                            </Form.Item>
                                        </td>
                                        <td>
                                            {!disabled && (
                                                <DeleteOutlined
                                                    style={{ color: "#D92D20", cursor: "pointer" }}
                                                    onClick={() => remove(name)}
                                                />
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}

                    {fields.length > 0 && (
                        <div className={styles.shareTotalRow}>
                            <span>{t("employeeFund.label.totalShare") || "รวมสัดส่วน"}</span>
                            <span className={isComplete ? styles.shareTotalOk : styles.shareTotalBad}>{totalShare}%</span>
                        </div>
                    )}
                </>
            )}
        </Form.List>
    );
};

export default BeneficiaryTable;
